// Admin panel hooks — queries and mutations for /admin/* endpoints.
//
// Write mutations require an X-StepUp header; callers get the token via
// the StepUpContext and pass it as `stepUpToken` on the mutation input.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiBlob } from "@/lib/api";
import type {
  AdminOrgDetail,
  AdminOrgsListResponse,
  AdminPlatformSettings,
  AdminResetPasswordResponse,
  AdminSystemToolsResponse,
  AdminTokenStats,
  AdminTokensListResponse,
  AdminUserDetail,
  AdminUsersListResponse,
  SystemEventsListResponse,
} from "@/types/admin";

function stepUpHeaders(token: string): Record<string, string> {
  return { "X-StepUp": token };
}

// ─── Users ────────────────────────────────────────────────────────────────────

export interface UsersListFilters {
  q?: string;
  is_active?: string;
  is_superuser?: string;
  has_2fa?: string;
  sort_by?: string;
  order?: "asc" | "desc";
  limit: number;
  offset: number;
}

export function useAdminUsers(filters: UsersListFilters) {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.is_active) params.set("is_active", filters.is_active);
  if (filters.is_superuser) params.set("is_superuser", filters.is_superuser);
  if (filters.has_2fa) params.set("has_2fa", filters.has_2fa);
  if (filters.sort_by) {
    params.set("sort_by", filters.sort_by);
    params.set("order", filters.order ?? "asc");
  }
  params.set("limit", String(filters.limit));
  params.set("offset", String(filters.offset));
  const query = params.toString();
  return useQuery({
    queryKey: ["admin", "users", query],
    queryFn: () => api<AdminUsersListResponse>(`/admin/users?${query}`),
  });
}

export function useAdminUserDetail(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", "detail", userId],
    queryFn: () => api<AdminUserDetail>(`/admin/users/${userId}`),
    enabled: userId != null,
  });
}

function invalidateUsers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["admin", "users"] });
}

export function useToggleUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      enable,
      stepUpToken,
    }: {
      userId: string;
      enable: boolean;
      stepUpToken: string;
    }) =>
      api(`/admin/users/${userId}/${enable ? "enable" : "disable"}`, {
        method: "POST",
        headers: stepUpHeaders(stepUpToken),
      }),
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useMarkEmailVerified() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      stepUpToken,
    }: {
      userId: string;
      stepUpToken: string;
    }) =>
      api(`/admin/users/${userId}/mark-email-verified`, {
        method: "POST",
        headers: stepUpHeaders(stepUpToken),
      }),
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      stepUpToken,
    }: {
      userId: string;
      stepUpToken: string;
    }) =>
      api<AdminResetPasswordResponse>(`/admin/users/${userId}/reset-password`, {
        method: "POST",
        headers: stepUpHeaders(stepUpToken),
      }),
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useSetSuperuser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      isSuperuser,
      stepUpToken,
    }: {
      userId: string;
      isSuperuser: boolean;
      stepUpToken: string;
    }) =>
      api(`/admin/users/${userId}`, {
        method: "PATCH",
        headers: stepUpHeaders(stepUpToken),
        body: { is_superuser: isSuperuser },
      }),
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useAnonymizeUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      reason,
      stepUpToken,
    }: {
      userId: string;
      reason: string;
      stepUpToken: string;
    }) =>
      api(
        `/admin/users/${userId}?reason=${encodeURIComponent(reason)}`,
        { method: "DELETE", headers: stepUpHeaders(stepUpToken) },
      ),
    onSuccess: () => invalidateUsers(qc),
  });
}

// ─── Orgs ─────────────────────────────────────────────────────────────────────

export interface OrgsListFilters {
  q?: string;
  sort_by?: string;
  order?: "asc" | "desc";
  limit: number;
  offset: number;
}

export function useAdminOrgs(filters: OrgsListFilters) {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.sort_by) {
    params.set("sort_by", filters.sort_by);
    params.set("order", filters.order ?? "asc");
  }
  params.set("limit", String(filters.limit));
  params.set("offset", String(filters.offset));
  const query = params.toString();
  return useQuery({
    queryKey: ["admin", "orgs", query],
    queryFn: () => api<AdminOrgsListResponse>(`/admin/orgs?${query}`),
  });
}

export function useAdminOrgDetail(orgId: string | null) {
  return useQuery({
    queryKey: ["admin", "orgs", "detail", orgId],
    queryFn: () => api<AdminOrgDetail>(`/admin/orgs/${orgId}`),
    enabled: orgId != null,
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      description,
      stepUpToken,
    }: {
      name: string;
      description: string;
      stepUpToken: string;
    }) =>
      api("/admin/orgs", {
        method: "POST",
        headers: stepUpHeaders(stepUpToken),
        body: { name, description },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "orgs"] });
    },
  });
}

export function useDeleteOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      force,
      stepUpToken,
    }: {
      orgId: string;
      force: boolean;
      stepUpToken: string;
    }) =>
      api(`/admin/orgs/${orgId}${force ? "?force=true" : ""}`, {
        method: "DELETE",
        headers: stepUpHeaders(stepUpToken),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "orgs"] });
    },
  });
}

// ─── Tokens ───────────────────────────────────────────────────────────────────

export interface TokensListFilters {
  user_id?: string;
  include_revoked?: boolean;
  limit: number;
  offset: number;
}

export function useAdminTokens(filters: TokensListFilters) {
  const params = new URLSearchParams();
  if (filters.user_id) params.set("user_id", filters.user_id);
  if (filters.include_revoked) params.set("include_revoked", "true");
  params.set("limit", String(filters.limit));
  params.set("offset", String(filters.offset));
  const query = params.toString();
  return useQuery({
    queryKey: ["admin", "tokens", query],
    queryFn: () => api<AdminTokensListResponse>(`/admin/tokens?${query}`),
  });
}

export function useAdminTokenStats() {
  return useQuery({
    queryKey: ["admin", "tokens", "stats"],
    queryFn: () => api<AdminTokenStats>("/admin/tokens/stats"),
  });
}

export function useRevokeUserTokens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      reason,
      stepUpToken,
    }: {
      userId: string;
      reason: string;
      stepUpToken: string;
    }) =>
      api<{ revoked_count: number }>("/admin/tokens/revoke-user", {
        method: "POST",
        headers: stepUpHeaders(stepUpToken),
        body: { user_id: userId, reason },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tokens"] });
    },
  });
}

export function useRevokeAllTokens() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      reason,
      stepUpToken,
    }: {
      reason: string;
      stepUpToken: string;
    }) => {
      const qs = reason
        ? `?confirm=true&reason=${encodeURIComponent(reason)}`
        : "?confirm=true";
      return api<{ revoked_count: number }>(`/admin/tokens/revoke-all${qs}`, {
        method: "POST",
        headers: stepUpHeaders(stepUpToken),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tokens"] });
    },
  });
}

// ─── Settings ─────────────────────────────────────────────────────────────────

export function useAdminSettings() {
  return useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => api<AdminPlatformSettings>("/admin/settings"),
  });
}

export function useUpdateAdminSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      patch,
      stepUpToken,
    }: {
      patch: Partial<AdminPlatformSettings>;
      stepUpToken: string;
    }) =>
      api<AdminPlatformSettings>("/admin/settings", {
        method: "PATCH",
        headers: stepUpHeaders(stepUpToken),
        body: patch,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["admin", "settings"], data);
    },
  });
}

// ─── System (tool health) ────────────────────────────────────────────────────

export function useAdminSystemTools() {
  return useQuery({
    queryKey: ["admin", "system", "tools"],
    queryFn: () => api<AdminSystemToolsResponse>("/admin/system/tools"),
  });
}

export function useRefreshSystemTools() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<AdminSystemToolsResponse>("/admin/system/tools/refresh", {
        method: "POST",
        body: {},
      }),
    onSuccess: (data) => {
      qc.setQueryData(["admin", "system", "tools"], data);
    },
  });
}

// ─── User export ─────────────────────────────────────────────────────────────

export async function downloadUserExport(userId: string): Promise<unknown> {
  return api(`/admin/users/${userId}/export`);
}

/**
 * Filters for the bulk CSV export. Every field is optional — omitting one
 * means "both values" (e.g. no `is_active` exports active and disabled
 * alike). Superusers and anonymized users are excluded server-side and
 * can't be opted back in.
 */
export interface UsersCsvExportFilters {
  q?: string;
  is_active?: boolean;
  email_verified?: boolean;
  has_2fa?: boolean;
}

export async function downloadUsersCsv(
  filters: UsersCsvExportFilters = {},
): Promise<void> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const query = params.toString();
  const { blob, filename } = await apiBlob(
    `/admin/users/export.csv${query ? `?${query}` : ""}`,
  );

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? "users.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ─── System events (operational health dash) ─────────────────────────────────

export interface SystemEventsFilters {
  severity?: string;
  kind?: string;
  account?: string;
  status: "open" | "resolved" | "all";
  limit: number;
  offset: number;
}

export function useSystemEvents(filters: SystemEventsFilters) {
  const params = new URLSearchParams();
  if (filters.severity) params.set("severity", filters.severity);
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.account) params.set("account", filters.account);
  params.set("status", filters.status);
  params.set("limit", String(filters.limit));
  params.set("offset", String(filters.offset));
  const query = params.toString();
  return useQuery({
    queryKey: ["admin", "system-events", query],
    queryFn: () =>
      api<SystemEventsListResponse>(`/admin/system-events?${query}`),
  });
}

// Polled (60s) so the dash badge stays roughly live without websockets.
export function useUnresolvedCount() {
  return useQuery({
    queryKey: ["admin", "system-events", "unresolved-count"],
    queryFn: () =>
      api<{ unresolved: number }>("/admin/system-events/unresolved-count"),
    refetchInterval: 60_000,
  });
}

// Resolve is a low-stakes acknowledgement → no step-up token needed
// (matches the backend's require_admin_session, not require_stepup).
export function useResolveSystemEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      api(`/admin/system-events/${eventId}/resolve`, { method: "POST" }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "system-events"] }),
  });
}

// ─── Admin Dashboard analytics ────────────────────────────────

export interface AdminAnalytics {
  as_of: string;
  health: {
    unresolved_alerts: number;
    failed_scans_24h: number;
    failed_logins_24h: number;
    queue: { scans: number | null; enrich: number | null };
    storage: {
      db_bytes: number | null;
      redis_bytes: number | null;
      disk: { path: string; total_bytes: number; used_bytes: number; free_bytes: number; percent_used: number } | null;
    };
  };
  live: { scans_today: number; scans_in_flight: number; users_online: number };
  revenue: {
    mrr_cents: Record<string, number>;
    new_subs_week: number;
    cancels_week: number;
    trials_expiring_7d: number;
    plan_distribution: Record<string, number>;
    new_orgs_week: number;
    new_orgs_month: number;
    new_users_week: number;
    free_to_paid_pct: number;
  };
  users: {
    active_30d: number;
    mfa_pct: number;
    role_distribution: Record<string, number>;
  };
  ai: { triaged_week: number; false_positive_pct: number };
  totals: { orgs: number; users: number; engagements: number };
}

export function useAdminAnalytics() {
  return useQuery({
    queryKey: ["admin", "analytics"],
    queryFn: () => api<AdminAnalytics>("/admin/analytics"),
    refetchOnWindowFocus: true,
  });
}
